/**
 * Agent of Agents — main page.
 *
 * Claude-Code-style: no persistent rail. The platform sidebar stays put;
 * inside the page a compact header shows "+ New" and a clock icon that
 * opens a popover with search + recent sessions. The chat surface
 * occupies the full pane below.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, message } from 'antd';
import { ConfirmModal } from '@/components/common';
import { bidiIsolate } from '@/utils/bidi';
import { Sidebar } from '@/components/layout/Sidebar';
import { useLanguage } from '@hooks/useLanguage';
import { useFeature } from '@hooks/useFeature';
import { aoaApi } from '../services/aoaApi';
import type { AoAConversation, AoAMessage, AoAProposal } from '../types';
import type { ContextUsage } from '@/types/models';
import { AoAChatPanel } from '../components/AoAChatPanel';
import { HistoryPopover } from '../components/HistoryPopover';
import { InspectorProvider, useInspector, inspectorClamps } from '../components/InspectorContext';
import { AoaInspectorPanel } from '../components/AoaInspectorPanel';
import { AoAConfigModal } from '../components/AoAConfigModal';
import { GearIcon } from '../../../components/GearIcon';
import './AgentOfAgentsPage.css';

/**
 * Outer wrapper that mounts the inspector provider. The actual page
 * UI lives in `AgentOfAgentsPageInner` so it can call `useInspector()`.
 */
export const AgentOfAgentsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => (
  <InspectorProvider>
    <AgentOfAgentsPageInner embedded={embedded} />
  </InspectorProvider>
);

const AgentOfAgentsPageInner: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useLanguage();
  // Gate the "Edit" affordance on the per-tenant edit flag; the drawer edits the
  // AoA persona + attached knowledge for the whole tenant. The FILE switches
  // live in the same dialog under their own flag, so a tenant allowed files but
  // not persona editing still gets the gear - the dialog itself shows only the
  // tabs the caller may actually use.
  const canEditPersona = useFeature('modules.agent_of_agents.edit_prompt');
  const filesEnabled = useFeature('modules.agent_of_agents.file_authoring');
  const canEditAoA = canEditPersona || filesEnabled;
  const [configOpen, setConfigOpen] = useState(false);
  // Side-panel state is page-level. We have to clear it when the user
  // switches AoA conversations — otherwise the old conversation's
  // proposal card lingers in the panel while the chat reloads, and
  // any staged file uploads carry across (very confusing).
  const inspector = useInspector();
  const [sessions, setSessions] = useState<AoAConversation[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  // Pagination state for the history popover. The backend caps page
  // size at 200 and returns `has_more` based on COUNT(*) post-filter.
  // Pre-fix the frontend fetched once with no pagination and the
  // popover stopped surfacing rows past the backend's 100-row cap.
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false);
  // Search query — server-side filter over conversation_title. Lives
  // here (not in the popover) because the page owns the sessions
  // list, and a search change means we refetch from offset=0 against
  // the FILTERED set rather than client-side filtering whatever's
  // been loaded into memory. User instruction (2026-06-10): load
  // ONLY as the user scrolls; never preload all conversations.
  const [sessionsQuery, setSessionsQuery] = useState('');
  const sessionsQueryRef = useRef('');
  const SESSIONS_PAGE_SIZE = 50;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeMessages, setActiveMessages] = useState<AoAMessage[] | undefined>(undefined);
  // The context meter's opening reading for the active session.
  const [activeContextUsage, setActiveContextUsage] = useState<ContextUsage | null>(null);
  // The organization's AoA model slot (refreshed by every create / open and by
  // a save in the settings), and the model the server runs ONE conversation on,
  // kept with its id: the picker shows it only for that conversation, so a new
  // or switched-to session never starts on the previous one's model.
  const [modelConfig, setModelConfig] = useState<
    { default_model: string | null; allowed_models: string[] } | null
  >(null);
  const [conversationModel, setConversationModel] = useState<{ id: string; model: string | null } | null>(null);
  // Past proposals for the active conversation, keyed by proposal_id.
  // Loaded alongside the messages so hydrateFromMessages can re-insert
  // resolved proposal cards on refresh / session-resume.
  const [activeProposalsById, setActiveProposalsById] = useState<Record<string, AoAProposal> | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  // Deleting a session from the history list — the regular chat's confirm.
  const [pendingDelete, setPendingDelete] = useState<AoAConversation | null>(null);
  const [deleting, setDeleting] = useState(false);
  // One-shot guard so we don't double-create when StrictMode mounts twice in dev.
  const didBootstrap = useRef(false);

  // Refresh resets to page 1 with the CURRENT query (so external
  // refreshes — new conversation created, title updated, etc. — keep
  // the user's search context intact).
  const refreshSessions = useCallback(async () => {
    try {
      const res = await aoaApi.listConversations({
        limit: SESSIONS_PAGE_SIZE,
        offset: 0,
        q: sessionsQueryRef.current,
      });
      setSessions(res.conversations);
      setSessionsHasMore(res.has_more);
      return res.conversations;
    } catch {
      return [] as AoAConversation[];
    }
  }, [SESSIONS_PAGE_SIZE]);

  // loadMoreSessions: fetch the NEXT page based on current sessions
  // length and append. Carries the same `q` as the initial load so
  // pagination follows the same filtered result-set.
  const loadMoreSessions = useCallback(async () => {
    if (sessionsLoadingMore || !sessionsHasMore) return;
    setSessionsLoadingMore(true);
    try {
      const res = await aoaApi.listConversations({
        limit: SESSIONS_PAGE_SIZE,
        offset: sessions.length,
        q: sessionsQueryRef.current,
      });
      // Dedupe by id in case a new conversation was created between
      // pages (it would land at offset 0 AND at the end of the next
      // page — without dedup we'd render it twice).
      setSessions((prev) => {
        const seen = new Set(prev.map((s) => s.conversation_id));
        const append = res.conversations.filter(
          (s) => !seen.has(s.conversation_id),
        );
        return [...prev, ...append];
      });
      setSessionsHasMore(res.has_more);
    } finally {
      setSessionsLoadingMore(false);
    }
  }, [sessions.length, sessionsHasMore, sessionsLoadingMore, SESSIONS_PAGE_SIZE]);

  // Initial load, and RESUME where they left off.
  //
  // The list is ordered last_message_at DESC, so [0] is the conversation they
  // were last in. Without selecting it the page always opened on the welcome
  // card, and a build in progress looked lost: the admin steps out to the
  // Library to upload a file — exactly what the Agent of Agents asks them to
  // do — comes back, and the conversation and its pending upload card are off
  // screen. Nothing was ever gone (picking the session in the sidebar restores
  // it, cards and their accept/reject status included) but nobody should have
  // to know that. "New chat" is one click away for a fresh start.
  //
  // Only a session with messages is resumed: an EMPTY one carries nothing to
  // come back to, and auto-opening it silently put the next question into an
  // untitled session left from days earlier (staging, 2026-10-05). A session
  // made in THIS visit is opened by "New session" itself, not by this.
  //
  // The functional update is load-bearing: the await opens a window in which
  // the user can hit "New chat", and a plain set would clobber the conversation
  // they just created.
  useEffect(() => {
    if (didBootstrap.current) return;
    didBootstrap.current = true;
    (async () => {
      setLoadingSessions(true);
      try {
        const convs = await refreshSessions();
        setActiveId((current) => current
          ?? convs.find((c) => c.message_count > 0)?.conversation_id
          ?? null);
      } finally {
        setLoadingSessions(false);
      }
    })();
  }, [refreshSessions]);

  // Search: debounced refetch from offset=0 with the new query. The
  // ref tracks the LATEST query so a stale closure inside loadMore
  // doesn't ship the previous query's filter.
  useEffect(() => {
    sessionsQueryRef.current = sessionsQuery;
    // Skip the initial bootstrap pass — the empty-query case is
    // already covered by the mount effect above.
    if (!didBootstrap.current) return;
    const handle = window.setTimeout(async () => {
      setLoadingSessions(true);
      try {
        const res = await aoaApi.listConversations({
          limit: SESSIONS_PAGE_SIZE,
          offset: 0,
          q: sessionsQuery,
        });
        // Only apply if the query is still current — if the user
        // typed again in the debounce window, abandon this result.
        if (sessionsQueryRef.current === sessionsQuery) {
          setSessions(res.conversations);
          setSessionsHasMore(res.has_more);
        }
      } finally {
        if (sessionsQueryRef.current === sessionsQuery) {
          setLoadingSessions(false);
        }
      }
    }, 220);  // ~typing-pause duration; balances "feels live" vs request flood
    return () => window.clearTimeout(handle);
  }, [sessionsQuery, SESSIONS_PAGE_SIZE]);

  // Load messages + proposals for the active session in parallel.
  // We need BOTH so hydrateFromMessages can re-insert past proposal
  // cards with their final status — otherwise refreshing the page
  // leaves the user looking at orphan tool-call blocks with no card.
  useEffect(() => {
    // Conversation switch — wipe the side panel so the previous
    // conversation's active proposal / queue / staged uploads don't
    // bleed into the new one. The hydration below will re-fetch any
    // proposals for the new conversation; the inline chat markers
    // remain (they're driven by the timeline reducer, not the inspector).
    inspector.resetForNewConversation();
    if (!activeId) {
      setActiveMessages(undefined);
      setActiveProposalsById(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [conv, proposals] = await Promise.all([
          aoaApi.getConversation(activeId),
          aoaApi.listProposals(activeId).catch(() => [] as AoAProposal[]),
        ]);
        if (cancelled) return;
        setActiveMessages(conv.messages || []);
        setActiveContextUsage(((conv as unknown as { context_usage?: ContextUsage }).context_usage) ?? null);
        if (conv.model_config) setModelConfig(conv.model_config);
        setConversationModel({ id: activeId, model: conv.effective_model ?? null });
        const byId: Record<string, AoAProposal> = {};
        for (const p of proposals) byId[p.proposal_id] = p;
        setActiveProposalsById(byId);
      } catch {
        if (!cancelled) {
          setActiveMessages([]);
          setActiveProposalsById({});
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire on conversation switch
  }, [activeId]);

  const onNewSession = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const conv = await aoaApi.createConversation();
      // A new conversation starts on the organization's default as it is NOW.
      if (conv.model_config) setModelConfig(conv.model_config);
      setConversationModel({ id: conv.conversation_id, model: conv.effective_model ?? null });
      setSessions((prev) => [conv, ...prev]);
      setActiveId(conv.conversation_id);
      setActiveMessages([]);
    } finally {
      setCreating(false);
    }
  }, [creating]);

  // Deleting the open session leaves the page as a fresh visit does: the
  // welcome card with "New session" — nothing of the deleted session (its
  // chat, side panel, or model) is carried into the next one.
  const confirmDeleteSession = useCallback(async () => {
    if (!pendingDelete) return;
    const deletedId = pendingDelete.conversation_id;
    setDeleting(true);
    try {
      await aoaApi.deleteConversation(deletedId);
      setSessions((prev) => prev.filter((s) => s.conversation_id !== deletedId));
      setActiveId((current) => (current === deletedId ? null : current));
      setPendingDelete(null);
    } catch (error) {
      console.error('[AgentOfAgents] Failed to delete session:', error);
      message.error(t('metaAgent.history.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, t]);

  const onTitleUpdated = useCallback((conversationId: string, title: string) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.conversation_id === conversationId ? { ...s, conversation_title: title } : s,
      ),
    );
  }, []);

  const onTurnComplete = useCallback(() => {
    refreshSessions();
  }, [refreshSessions]);

  const aoaBody = (
    <main className="main-content aoa-main">
          {/* Top bar — mirrors the regular conversation page header.
              Brand/agent name on the LEFT (like `JewishPro` in the
              regular chat), action icons on the RIGHT. */}
          <div className="aoa-topbar">
            <div className="aoa-topbar-brand">
              {t('metaAgent.title') || 'Agent of Agents'}
            </div>
            <div className="aoa-topbar-actions">
              {canEditAoA && (
                <Tooltip
                  title={t('metaAgent.config.editTooltip') || 'Edit Agent of Agents'}
                  placement="bottom"
                  overlayClassName="aoa-tooltip"
                >
                  <button
                    type="button"
                    className="aoa-header-icon-btn"
                    onClick={() => setConfigOpen(true)}
                    aria-label={t('metaAgent.config.editTooltip') || 'Edit Agent of Agents'}
                    data-testid="aoa-edit-config"
                  >
                    <GearIcon size={14} />
                  </button>
                </Tooltip>
              )}
              <HistoryPopover
                sessions={sessions}
                activeId={activeId}
                onSelect={setActiveId}
                loading={loadingSessions}
                hasMore={sessionsHasMore}
                loadingMore={sessionsLoadingMore}
                onLoadMore={loadMoreSessions}
                query={sessionsQuery}
                onQueryChange={setSessionsQuery}
                onDelete={setPendingDelete}
              />
              <Tooltip
                title={t('metaAgent.newConversation') || 'New session'}
                placement="bottom"
                overlayClassName="aoa-tooltip"
              >
                <button
                  type="button"
                  className="aoa-header-icon-btn"
                  onClick={onNewSession}
                  disabled={creating}
                  aria-label={t('metaAgent.newConversation') || 'New session'}
                  data-testid="aoa-new-session"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Chat (left) + inspector panel (right) split. The split
              is owned by the page (not by either panel) so the drag
              handle has a single reference frame. */}
          <SplitLayout>
            {/* LEFT — chat or welcome */}
            {activeId ? (
              <AoAChatPanel
                key={activeId}
                conversationId={activeId}
                initialMessages={activeMessages}
                initialProposalsById={activeProposalsById}
                initialContextUsage={activeContextUsage}
                modelConfig={modelConfig}
                initialModel={conversationModel?.id === activeId ? conversationModel.model : null}
                onTitleUpdated={onTitleUpdated}
                onTurnComplete={onTurnComplete}
              />
            ) : (
              <div className="aoa-welcome">
                <div className="aoa-welcome-card">
                  <h2 className="aoa-welcome-title">
                    {t('metaAgent.emptyState.title') || 'What should we work on?'}
                  </h2>
                  <p className="aoa-welcome-sub">
                    {t('metaAgent.emptyState.subtitle') ||
                      'Ask me to create an agent, refine a skill, or test how an agent handles a scenario.'}
                  </p>
                  <div className="aoa-welcome-actions">
                    <button
                      type="button"
                      className="aoa-welcome-btn-primary"
                      onClick={onNewSession}
                      disabled={creating}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      <span>
                        {creating
                          ? (t('common.loading') || 'Starting…')
                          : (t('metaAgent.newConversation') || 'New session')}
                      </span>
                    </button>
                  </div>
                  {sessions.length > 0 && (
                    <div className="aoa-welcome-hint">
                      {t('metaAgent.resumeHint') ||
                        'Or resume a past session from the clock icon above.'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </SplitLayout>
          <ConfirmModal
            isOpen={pendingDelete !== null}
            onClose={() => setPendingDelete(null)}
            onConfirm={confirmDeleteSession}
            title={t('sidebar.deleteConversation')}
            message={t('sidebar.deleteConversationConfirm', {
              title: bidiIsolate(pendingDelete?.conversation_title || t('metaAgent.untitledSession')),
            })}
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            variant="danger"
            loading={deleting}
          />
          {canEditAoA && (
            <AoAConfigModal
              open={configOpen}
              onClose={() => setConfigOpen(false)}
              onModelSaved={setModelConfig}
            />
          )}
    </main>
  );

  // Embedded mode (rendered as a tab inside the Agents page): no Sidebar /
  // page chrome — the AoA body fills the host tab's content column.
  if (embedded) return aoaBody;

  return (
    <div className="admin-container">
      <Sidebar />
      <div className="main-container">{aoaBody}</div>
    </div>
  );
};

/**
 * Resizable split layout for the AoA page: chat on the left, inspector
 * panel on the right, a 4px drag handle in between. The handle, when
 * dragged, updates `inspector.widthFraction` (persisted in localStorage
 * by the context). When the inspector is collapsed, the panel becomes
 * a thin vertical rail and the chat takes the rest.
 */
const SplitLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useLanguage();
  const inspector = useInspector();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const draggingRef = React.useRef(false);
  const [draggingState, setDraggingState] = useState(false);

  // Drag handle math: translate pointer X into a fraction of the
  // container width (the chat region is [0, 1 - fraction]; panel is
  // [1 - fraction, 1]). Clamping happens inside setWidthFraction.
  const onDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (inspector.collapsed) return;
    e.preventDefault();
    draggingRef.current = true;
    setDraggingState(true);
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  }, [inspector.collapsed]);

  const onDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const xFromRight = rect.right - e.clientX;
    const frac = xFromRight / rect.width;
    inspector.setWidthFraction(frac);
  }, [inspector]);

  const onDragEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDraggingState(false);
    try { (e.currentTarget as Element).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  }, []);

  // Double-clicking the handle resets to the default width — common
  // pattern in IDEs / chat clients with resizable panels.
  const onHandleDoubleClick = useCallback(() => {
    inspector.setWidthFraction(inspectorClamps.DEFAULT);
  }, [inspector]);

  // CSS grid columns: chat | handle | panel (or rail when collapsed).
  // We use percentages on the fraction so the layout reflows smoothly
  // on viewport resize without us having to track pixels.
  const panelFraction = inspector.collapsed ? 0 : inspector.widthFraction;
  const chatColumn = `minmax(0, ${(1 - panelFraction) * 100}%)`;
  const panelColumn = inspector.collapsed
    ? '44px'
    : `minmax(0, ${panelFraction * 100}%)`;
  const handleColumn = inspector.collapsed ? '0' : '4px';
  const gridTemplate = `${chatColumn} ${handleColumn} ${panelColumn}`;

  return (
    <div
      ref={containerRef}
      className={`aoa-split${draggingState ? ' is-dragging' : ''}${inspector.collapsed ? ' is-collapsed' : ''}`}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      <div className="aoa-split__chat">{children}</div>
      {!inspector.collapsed && (
        <div
          className="aoa-split__handle"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('metaAgent.resizeInspector')}
          aria-valuenow={Math.round(panelFraction * 100)}
          aria-valuemin={Math.round(inspectorClamps.MIN * 100)}
          aria-valuemax={Math.round(inspectorClamps.MAX * 100)}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          onDoubleClick={onHandleDoubleClick}
          tabIndex={0}
          onKeyDown={(e) => {
            // Keyboard accessibility: left/right shifts the divider 2% at a time.
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              inspector.setWidthFraction(panelFraction + 0.02);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              inspector.setWidthFraction(panelFraction - 0.02);
            } else if (e.key === 'Home') {
              e.preventDefault();
              inspector.setWidthFraction(inspectorClamps.DEFAULT);
            }
          }}
        >
          <span className="aoa-split__handle-grip" aria-hidden="true" />
        </div>
      )}
      <AoaInspectorPanel />
    </div>
  );
};

export default AgentOfAgentsPage;
